<template>
  <InstructorLayout>
    <div class="instructor-history">
      <h1>📋 労務履歴</h1>
      <p class="page-description">
        いつ・何件の仕事を行ったか、いくらになったかを確認できます。税金計算などにご利用ください。
      </p>

      <!-- 読み込み中 -->
      <div v-if="isLoading" class="loading">
        <p>履歴を読み込み中...</p>
      </div>

      <template v-else>
        <!-- サマリ -->
        <div class="summary-cards">
          <div class="summary-card">
            <span class="summary-label">合計件数</span>
            <span class="summary-value">{{ filteredTotalCount }}件</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">合計金額</span>
            <span class="summary-value">{{ formattedTotalAmount }}</span>
          </div>
        </div>

        <!-- フィルタ（年・月） -->
        <div class="filters">
          <label class="filter-item">
            <span class="filter-label">年</span>
            <select v-model="filterYear" class="filter-select" @change="applyFilters">
              <option value="">すべて</option>
              <option v-for="y in availableYears" :key="y" :value="y">{{ y }}年</option>
            </select>
          </label>
          <label class="filter-item">
            <span class="filter-label">月</span>
            <select v-model="filterMonth" class="filter-select" @change="applyFilters">
              <option value="">すべて</option>
              <option v-for="m in 12" :key="m" :value="m">{{ m }}月</option>
            </select>
          </label>
        </div>

        <!-- 日別一覧 -->
        <div class="history-table-wrap">
          <table class="history-table" aria-label="日別労務一覧">
            <thead>
              <tr>
                <th>日付（JST）</th>
                <th class="num">完了件数</th>
                <th class="num">金額</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="filteredRows.length === 0">
                <td colspan="3" class="empty">該当する履歴がありません</td>
              </tr>
              <tr v-for="row in filteredRows" :key="row.date" class="data-row">
                <td>{{ formatDateJp(row.date) }}</td>
                <td class="num">{{ row.count }}件</td>
                <td class="num">{{ formatYen(row.amount) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- グローバルナビゲーション -->
      <InstructorNavigation />
    </div>
  </InstructorLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { supabase } from '../../supabaseClient'
import InstructorLayout from '../../components/layout/InstructorLayout.vue'
import InstructorNavigation from '../../components/layout/InstructorNavigation.vue'

// 1件あたりの単価（円）。将来は設定画面やDBから取得可能にする
const YEN_PER_FEEDBACK = 150

const isLoading = ref(false)
const rawRows = ref([]) // { date: 'YYYY-MM-DD', count, amount }[]
const filterYear = ref('')
const filterMonth = ref('')

// 日本時間で YYYY-MM-DD を取得
function toJstDateString(isoString) {
  if (!isoString) return ''
  return new Date(isoString).toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })
}

// 日付ごとに集計した行のリスト（日付降順）
const dailyRows = computed(() => rawRows.value)

// フィルタ適用後の行
const filteredRows = computed(() => {
  let rows = [...dailyRows.value]
  if (filterYear.value) {
    rows = rows.filter((r) => r.date.startsWith(String(filterYear.value)))
  }
  if (filterMonth.value) {
    const m = String(filterMonth.value).padStart(2, '0')
    rows = rows.filter((r) => r.date.slice(5, 7) === m)
  }
  return rows
})

// フィルタ適用後の合計件数・金額
const filteredTotalCount = computed(() =>
  filteredRows.value.reduce((acc, r) => acc + r.count, 0)
)
const filteredTotalAmount = computed(() =>
  filteredRows.value.reduce((acc, r) => acc + r.amount, 0)
)
const formattedTotalAmount = computed(() => formatYen(filteredTotalAmount.value))

// データに含まれる年を列挙（フィルタ用）
const availableYears = computed(() => {
  const set = new Set(rawRows.value.map((r) => r.date.slice(0, 4)))
  return Array.from(set).sort((a, b) => Number(b) - Number(a))
})

function applyFilters() {
  // リアクティブなので特に処理不要（computed で反映）
}

function formatDateJp(dateStr) {
  if (!dateStr) return ''
  const [y, m, d] = dateStr.split('-')
  return `${y}年${Number(m)}月${Number(d)}日`
}

function formatYen(value) {
  return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(value ?? 0)
}

async function loadHistory() {
  isLoading.value = true
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.id) {
      rawRows.value = []
      return
    }

    const { data, error } = await supabase
      .from('submissions_v3')
      .select('feedback_submitted_at')
      .eq('type', 'assignment')
      .eq('status', 'feedback_completed')
      .eq('feedback_instructor_id', user.id)
      .not('feedback_submitted_at', 'is', null)
      .order('feedback_submitted_at', { ascending: false })

    if (error) {
      console.error('労務履歴取得エラー:', error)
      rawRows.value = []
      return
    }

    // 日本時間の日付でグループ化
    const byDate = new Map()
    for (const row of data || []) {
      const date = toJstDateString(row.feedback_submitted_at)
      if (!date) continue
      const prev = byDate.get(date) || 0
      byDate.set(date, prev + 1)
    }

    const rows = Array.from(byDate.entries())
      .map(([date, count]) => ({ date, count, amount: count * YEN_PER_FEEDBACK }))
      .sort((a, b) => (b.date > a.date ? 1 : -1))

    rawRows.value = rows
  } catch (err) {
    console.error('労務履歴読み込み例外:', err)
    rawRows.value = []
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  loadHistory()
})
</script>

<style scoped>
.instructor-history {
  max-width: 900px;
  margin: 0 auto;
  padding: 1rem;
  padding-bottom: 5rem; /* 下部ナビ分 */
}

h1 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #1f2937;
}

.page-description {
  color: #6b7280;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
}

.loading {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.summary-cards {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.summary-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  min-width: 140px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.summary-label {
  display: block;
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
}

.filters {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-label {
  font-size: 0.9rem;
  color: #374151;
}

.filter-select {
  padding: 0.4rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
}

.history-table-wrap {
  overflow-x: auto;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
}

.history-table th,
.history-table td {
  padding: 0.75rem 1rem;
  text-align: left;
  border-bottom: 1px solid #f3f4f6;
}

.history-table th {
  background: #f9fafb;
  color: #374151;
  font-weight: 600;
}

.history-table th.num,
.history-table td.num {
  text-align: right;
}

.history-table tbody tr:hover {
  background: #f9fafb;
}

.history-table .empty {
  text-align: center;
  color: #9ca3af;
  padding: 2rem;
}

.data-row td:first-child {
  white-space: nowrap;
}
</style>
